import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { useTour } from "@/lib/tour";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Compass, X } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function GettingStarted() {
  const { update } = useSettings() || {};
  const { startTour } = useTour();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/getting-started").then(({ data }) => setData(data)).catch((err) => console.error("Could not load checklist", err));
  }, []);

  if (!data || hidden || data.dismissed || data.completed === data.total) return null;

  const dismiss = async () => {
    setHidden(true);
    try { await update?.({ checklist_dismissed: true }); }
    catch (err) { console.error("Could not save checklist state", err); }
  };

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <section className="studio-card p-5 md:p-6" data-testid="getting-started-card">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--studio-primary))] font-medium">Getting started</div>
          <h2 className="text-base md:text-lg font-semibold tracking-tight mt-1">Five things to try</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {data.completed} of {data.total} done — this list ticks itself off as you work.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={startTour} data-testid="checklist-start-tour">
            <Compass className="w-3.5 h-3.5 mr-1.5" /> Take the tour
          </Button>
          <button onClick={dismiss} data-testid="checklist-dismiss" className="text-muted-foreground hover:text-foreground" title="Hide checklist">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-4" data-testid="checklist-progress">
        <div className="h-full bg-[hsl(var(--studio-primary))] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mt-4">
        {data.items.map((it) => (
          <Link
            key={it.key}
            to={it.route}
            data-testid={`checklist-item-${it.key}`}
            className={cn(
              "flex items-start gap-2.5 p-3 rounded-xl border transition-colors",
              it.done
                ? "border-transparent bg-[hsl(var(--halo-teal)/0.07)]"
                : "border-border hover:border-[hsl(var(--studio-primary)/0.4)] hover:bg-[hsl(var(--studio-primary)/0.03)]"
            )}
          >
            {it.done
              ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--halo-teal))] mt-0.5 shrink-0" />
              : <Circle className="w-4 h-4 text-muted-foreground/50 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className={cn("text-sm font-medium leading-snug", it.done && "text-muted-foreground line-through")}>{it.title}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{it.description}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
